import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { useLocalizedPath } from "../components/Header";
import { Shield } from "lucide-react";

export default function Datenschutz() {
  const { t } = useTranslation();
  const { lang = 'de' } = useParams();
  const { getPath } = useLocalizedPath();

  const websiteVisitItems = t('privacy.collection.websiteVisit.items', { returnObjects: true }) || [];
  const purposeItems = t('privacy.collection.websiteVisit.purposeItems', { returnObjects: true }) || [];
  const disclosureItems = t('privacy.disclosure.items', { returnObjects: true }) || [];

  return (
    <div data-testid="datenschutz-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h1 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white">
            {t('privacy.title')}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 bg-[#F7FAFC]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Card className="bg-white border">
            <CardContent className="p-6 md:p-10">
              <div className="prose prose-gray max-w-none">
                {/* Einleitung */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="h-6 w-6 text-orange-500" />
                    <h2 className="text-2xl font-bold font-['Manrope'] text-[#2D3748] m-0">
                      {t('privacy.overview.title')}
                    </h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.overview.description')}
                  </p>
                </div>

                {/* Hinweis für nicht-deutsche Versionen */}
                {lang !== 'de' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-8">
                    <p className="text-gray-700 text-sm m-0">
                      {t('privacy.legalNote')}
                    </p>
                  </div>
                )}

                {/* 1. Verantwortlicher */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('privacy.responsible.title')}
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 m-0">
                      MT Renovierung & Innenausbau<br />
                      Maicol Talamo<br />
                      Uelzen und Umgebung<br /><br />
                      {t('common.phone')}: +49 152 07684235<br />
                      {t('common.email')}: info@mtrenovierung.de
                    </p>
                  </div>
                </div>

                {/* Datenschutzbeauftragter */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('privacy.dpo.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.dpo.description')}
                  </p>
                </div>

                {/* 2. Erhebung und Speicherung */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('privacy.collection.title')}
                  </h3>
                  
                  <h4 className="text-lg font-medium text-[#2D3748] mt-4 mb-2">
                    {t('privacy.collection.websiteVisit.title')}
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {t('privacy.collection.websiteVisit.description')}
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                    {Array.isArray(websiteVisitItems) && websiteVisitItems.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.collection.websiteVisit.purposes')}
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    {Array.isArray(purposeItems) && purposeItems.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {t('privacy.collection.websiteVisit.legalBasis')}
                  </p>

                  <h4 className="text-lg font-medium text-[#2D3748] mt-6 mb-2">
                    {t('privacy.collection.emailContact.title')}
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.collection.emailContact.description')}
                  </p>
                </div>

                {/* 3. Weitergabe von Daten */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('privacy.disclosure.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.disclosure.description')}
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
                    {Array.isArray(disclosureItems) && disclosureItems.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                {/* 4. Cookies */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('privacy.cookies.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {t('privacy.cookies.description1')}
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {t('privacy.cookies.description2')}
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.cookies.moreInfo')}
                  </p>
                </div>

                {/* 5. Hosting */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('privacy.hosting.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.hosting.description1')}
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {t('privacy.hosting.description2')}
                  </p>
                </div>

                {/* 6. Betroffenenrechte */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('privacy.rights.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {t('privacy.rights.description')}
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>
                      <strong>{t('privacy.rights.access.title')}</strong>{" "}
                      {t('privacy.rights.access.description')}
                    </li>
                    <li>
                      <strong>{t('privacy.rights.rectification.title')}</strong>{" "}
                      {t('privacy.rights.rectification.description')}
                    </li>
                    <li>
                      <strong>{t('privacy.rights.deletion.title')}</strong>{" "}
                      {t('privacy.rights.deletion.description')}
                    </li>
                    <li>
                      <strong>{t('privacy.rights.restriction.title')}</strong>{" "}
                      {t('privacy.rights.restriction.description')}
                    </li>
                    <li>
                      <strong>{t('privacy.rights.portability.title')}</strong>{" "}
                      {t('privacy.rights.portability.description')}
                    </li>
                    <li>
                      <strong>{t('privacy.rights.objection.title')}</strong>{" "}
                      {t('privacy.rights.objection.description')}
                    </li>
                    <li>
                      <strong>{t('privacy.rights.complaint.title')}</strong>{" "}
                      {t('privacy.rights.complaint.description')}
                    </li>
                  </ul>
                </div>

                {/* 7. Widerruf */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('privacy.revocation.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.revocation.description')}
                  </p>
                </div>

                {/* 8. Aktualität */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('privacy.updates.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.updates.description1')}
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {t('privacy.updates.description2')}
                  </p>
                </div>
              </div>

              {/* Links */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <p className="text-gray-600 text-sm">
                  {t('impressum.moreInfo')}{" "}
                  <Link to={getPath('impressum')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('impressum.title')}
                  </Link>
                  ,{" "}
                  <Link to={getPath('cookies')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('cookiePolicy.title')}
                  </Link>
                  {" "}&{" "}
                  <Link to={getPath('disclaimer')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('disclaimer.title')}
                  </Link>
                  .
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
